/**
 * ArTableContextMenu Component
 *
 * Provides a right-click context menu for ArTable with table-level actions.
 * Actions: Add Row.
 *
 * Renders nothing but its children inside the PDF export portal (SYD-2441). The
 * portal draws a second copy of every table, and `ArTableExportModals` reads the
 * open flags off the `uiState` slot BOTH copies share, so an open export modal
 * made the copy mount a second `FormExportModal` — a second portal, a second
 * `FormUI`, every table again. That recursion is unbounded: the trace shows one
 * level per Firestore snapshot delivery, four documents and 670ms of blocked main
 * thread per level, until typing stops landing. A hidden capture has no
 * right-click and no modals of its own, so the whole wrapper goes.
 */

import { FC, ReactNode, useState, useId, useContext } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { ArTableActionsContext, ArTableUIStateContext } from '../context';
import { ContextMenu } from '@/providers/ContextMenu';
import { usePdfExportMode } from '../usePdfExportMode';
import { useArTableExportActions, ArTableExportModals } from '../export';
import type { ArRowId, ArTableId } from '#types/System.Artifacts.types';
import type { UIArTableColumnOverride } from '#types/System.types';

type ArTableContextMenuProps = {
  tableId: ArTableId;
  parentPath: string;
  disabled?: boolean;
  columnConfig?: UIArTableColumnOverride[];
  children: ReactNode;
};

/**
 * ArTableContextMenu wraps table content and provides right-click context menu.
 */
const ArTableContextMenu: FC<ArTableContextMenuProps> = ({
  tableId,
  parentPath,
  disabled = false,
  columnConfig,
  children,
}) => {
  const menuId = useId();
  const actions = useContext(ArTableActionsContext)!;
  const uiState = useContext(ArTableUIStateContext)!;
  const [adding, setAdding] = useState(false);
  const isPdfExportMode = usePdfExportMode();
  const { exportMenuItems } = useArTableExportActions({ tableId, parentPath, disabled, columnConfig });

  if (isPdfExportMode) return <>{children}</>;

  const handleAddRow = async () => {
    setAdding(true);
    try {
      const renderedRowIds = uiState.renderedRowIds;
      const resolvedAfterRowId: ArRowId | undefined = renderedRowIds.length > 0
        ? renderedRowIds[renderedRowIds.length - 1] as ArRowId
        : undefined;

      const afterRowId = (
        resolvedAfterRowId &&
        renderedRowIds.length > 1 &&
        uiState.pagination.currentPage < uiState.pagination.totalPages &&
        resolvedAfterRowId === renderedRowIds[renderedRowIds.length - 1]
      )
        ? renderedRowIds[renderedRowIds.length - 2] as ArRowId
        : resolvedAfterRowId;

      await actions.addRow(undefined, afterRowId ? { afterRowId } : undefined);
    } finally {
      setAdding(false);
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'add-row',
      label: 'Add Row',
      icon: <PlusOutlined />,
      onClick: handleAddRow,
      disabled: disabled || adding,
    },
    { type: 'divider' },
    ...exportMenuItems ?? [],
  ];

  return (
    <>
      <ContextMenu menuId={menuId} items={items} disabled={disabled}>
        {children}
      </ContextMenu>
      <ArTableExportModals columnConfig={columnConfig} />
    </>
  );
};

export default ArTableContextMenu;
